import { AxiosResponse } from 'axios';
import { environment } from '../../../../environments/environment';
import { EndpointHelper } from '../endpoint.helper';
import { HttpClient } from '../http';
import { StorageHelper } from '../storage';
import { CacheAreaKey } from '../../config/cache-config';

export type PackageContent = {
  Code: string;
  PackageColor: string;
  SecondaryPackageColor: string;
  IconCode: string;
  SelfServiceIconCode: string;
  PackageExcerpt: string;
  Price: number;
  ProductName: string;
  BundleExcerpt: string;
  ExtraStreamPrice: number;
  StreamSavingsPrice: number;
  IncludesStream: boolean;
  TermsAndConditionDisclaimer: string;
  FeaturedChannels: number[];
  ProductImage: string;
};

export type ChannelContent = {
  Id: string;
  ChannelName: string;
  ChannelGenre: number[];
  ChannelLogo: string;
};

export type ChannelGenreContent = {
  Id: string;
  GenreName: string;
};

export class ContentService {
  private static apiBuilder = new EndpointHelper(`${environment.UMBRACO_BASE_URI}`);

  static getPackages(): Promise<PackageContent[]> {
    const url = this.apiBuilder
      .createUrl('products/packages')
      .addBaseRouteParam('global-site-configuration')
      .addRouteParam('ujson')
      .addQueryParam('children', 'Packages')
      .addQueryParam('select', 'Code,ProductName,ProductImage,Price,ExtraStreamPrice,PackageColor,SecondaryPackageColor,IconCode,SelfServiceIconCode,StreamSavingsPrice,IncludesStream,PackageExcerpt,BundleExcerpt,TermsAndConditionDisclaimer,FeaturedChannels,Genre')
      .addQueryParam('resolveToIds', 'FeaturedChannels')
      .addQueryParam('resolveMedia', 'ProductImage')
      .addQueryParam('noCache', true)
      .build();

    const storedItems = (StorageHelper<PackageContent[]>).GetInstance.getFromCache('cnt', `Packages`, CacheAreaKey.Content);
    if (storedItems) {
      return Promise.resolve(storedItems as PackageContent[]);
    }

    return HttpClient.GetInstance.get<PackageContent>(url)
      .then(
        (res: AxiosResponse<any>) => (StorageHelper<PackageContent[]>).GetInstance.setCacheAndReturn(
          'cnt',
          CacheAreaKey.Content,
          res?.data?.entities?.map((entity: any) => entity.properties),
          `Packages`
        ) as PackageContent[],
        err => {
          console.error('Failed to load content -> ', err)
          return [];
        });
  }

  static getChannels(): Promise<any> {
    const url = this.apiBuilder
      .createUrl('channels')
      .addBaseRouteParam('global-site-configuration')
      .addRouteParam('ujson')
      .addQueryParam('children', 'Channels')
      .addQueryParam('select', 'Id,ChannelName,ChannelLogo,ChannelGenre')
      .addQueryParam('resolveToIds', 'ChannelGenre')
      .addQueryParam('resolveMedia', 'ChannelLogo')
      .addQueryParam('noCache', true)
      .build();

      const storedItems = (StorageHelper<ChannelContent[]>).GetInstance.getFromCache('cnt', `Channels`, CacheAreaKey.Content);
      if (storedItems) {
        return Promise.resolve(storedItems as any);
      }
  
      return HttpClient.GetInstance.get<PackageContent>(url)
        .then(
          (res: AxiosResponse<any>) => (StorageHelper<ChannelContent[]>).GetInstance.setCacheAndReturn(
            'cnt',
            CacheAreaKey.Content,
            res?.data?.entities?.map((entity: any) => entity.properties),
            `Channels`
          ) as ChannelContent[],
          err => {
            console.error('Failed to load content -> ', err)
            return [];
          });
  }

  static getChannelGenres(): Promise<any> {
    const url = this.apiBuilder
      .createUrl('channel-genres')
      .addBaseRouteParam('global-site-configuration')
      .addRouteParam('ujson')
      .addQueryParam('children', 'ChannelGenres')
      .addQueryParam('select', 'Id,GenreName')
      .addQueryParam('noCache', true)
      .build();

      const storedItems = (StorageHelper<ChannelGenreContent[]>).GetInstance.getFromCache('cnt', `ChannelGenres`, CacheAreaKey.Content);
      if (storedItems) {
        return Promise.resolve(storedItems as any);
      }
  
      return HttpClient.GetInstance.get<PackageContent>(url)
        .then(
          (res: AxiosResponse<any>) => (StorageHelper<ChannelGenreContent[]>).GetInstance.setCacheAndReturn(
            'cnt',
            CacheAreaKey.Content,
            res?.data?.entities?.map((entity: any) => entity.properties),
            `ChannelGenres`
          ) as ChannelGenreContent[],
          err => {
            console.error('Failed to load content -> ', err)
            return [];
          });
  }
}
